"use client";

import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";

declare global {
  interface Window {
    Five9ChatPlugin: (options: Record<string, any>) => void;
    Five9ChatSetVariable: (key: string, value: string) => void;
    Five9ChatSetContact: (key: string, value: string) => void;
    Five9ChatAddAgentMessage: (message: string, agentId: number) => void;
    f9_postMessage: (message: Record<string, any>) => void;
  }
}

interface Five9ChatProps {
  configId: string;
  campaignName?: string;
  universitySfId: string;
  consentSchoolName: string;
  program?: string;
  campus?: string;
  cohort?: string;
  selected_program?: string;
  selected_campus?: string;
  selected_cohort?: string;
}

interface UserInfo {
  userFirstName: string;
  userLastName: string;
  userEmail: string;
  userPhoneNumber: string;
}

interface SelectedInfo {
  selected_programId: string;
  selected_campusId: string;
  selected_cohortId: string;
  programId: string;
  campusId: string;
  cohortId: string;
}

const Five9Chat: React.FC<Five9ChatProps> = (props) => {
  const [availableToChat, setAvailableToChat] = useState<boolean>(false);
  const [userInfo, setUserInfo] = useState<UserInfo>({
    userFirstName: "",
    userLastName: "",
    userEmail: "",
    userPhoneNumber: "",
  });
  const [selectedInfo, setSelectedInfo] = useState<SelectedInfo>({
    selected_programId: "",
    selected_campusId: "",
    selected_cohortId: "",
    programId: "",
    campusId: "",
    cohortId: "",
  });

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://live-chat.ps.five9.com/Five9ChatPlugin.js";
    script.async = true;
    script.onload = initializeChat;
    document.body.appendChild(script);

    return () => {
      setAvailableToChat(false);
      document.body.removeChild(script);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeChat = () => {
    const {
      configId,
      campaignName,
      universitySfId,
      consentSchoolName,
      program,
      campus,
      cohort,
      selected_program,
      selected_campus,
      selected_cohort,
    } = props;

    let attributes: Array<Record<string, string>> = [
      { "Social.university": universitySfId },
      { "Social.university_name": consentSchoolName },
      { "Social.leadsource": "Chat" },
      { "Social.skill": campaignName || "" },
      { "Social.ga_client_id": Cookies.get("_ga") || "" },
      { "Social.uuid": Cookies.get("Trilogy_UUID") || "" },
      { "Social.original_url": Cookies.get("original_url") || window.location.href.substring(0, 499) },
      { "Social.original_referrer": Cookies.get("original_referrer") || document.referrer.substring(0, 499) },
      { "Social.keywords": Cookies.get("keyword") || "" },
      { "Social.referrer": window.location.href.substring(0, 1000) },
      { "Social.utm_cost": getAttributionFields("utm_cost", "") },
      { "Social.utm_medium": getAttributionFields("utm_medium", "") },
      { "Social.utm_content": getAttributionFields("utm_content", "") },
      { "Social.utm_term": getAttributionFields("utm_term", "") },
      { "Social.utm_source": getAttributionFields("utm_source", "") },
      { "Social.utm_campaign": getAttributionFields("utm_campaign", "") },
      { "Social.utm_campaignname": getAttributionFields("utm_campaignname", "") },
      { "Social.utm_campaignid": getAttributionFields("utm_campaignid", "") },
      { "Social.utm_adsetname": getAttributionFields("utm_adsetname", "") },
      { "Social.utm_adsetid": getAttributionFields("utm_adsetid", "") },
      { "Social.utm_adid": getAttributionFields("utm_adid", "") },
      { "Social.utm_adgroupid": getAttributionFields("utm_adgroupid", "") },
      { "Social.utm_matchtype": getAttributionFields("utm_matchtype", "") },
      { "Social.utm_network": getAttributionFields("utm_network", "") },
      { "Social.utm_device": getAttributionFields("utm_device", "") },
      { "Social.utm_creative": getAttributionFields("utm_creative", "") },
      { "Social.utm_placement": getAttributionFields("utm_placement", "") },
      { "Social.utm_locationphysicalms": getAttributionFields("utm_locationphysicalms", "") },
      { "Social.fbclid": getAttributionFields("fbclid", "") },
      { "Social.mkwid": getAttributionFields("mkwid", "") },
    ];

    if (program) {
      attributes.push({ "Social.program": program });
    } else if (selected_program) {
      attributes.push({ "Social.program": selected_program });
    }

    if (campus) {
      attributes.push({ "Social.campus": campus });
    } else if (selected_campus) {
      attributes.push({ "Social.campus": selected_campus });
    }

    if (cohort) {
      attributes.push({ "Social.cohort": cohort });
    } else if (selected_cohort) {
      attributes.push({ "Social.cohort": selected_cohort });
    }

    let options: Record<string, any> = {
      appId: "2U BC",
      configId: configId,
      attributes: attributes,
    };

    if (campaignName) {
      options["campaignName"] = campaignName;
    }

    window.Five9ChatPlugin(options);
    setAvailableToChat(true);
  };

  const getAttributionFields = (name: string, defaultValue: string): string => {
    const params = new URLSearchParams(window.location.search);
    return Cookies.get(name) || params.get(name) || defaultValue;
  };

  useEffect(() => {
    if (typeof window !== "undefined" && window.Five9ChatSetVariable) {
      window.Five9ChatSetVariable("Social.university", props.universitySfId);
      window.Five9ChatSetVariable("Social.program", props.selected_program || props.program || "");
      window.Five9ChatSetVariable("Social.campus", props.selected_campus || props.campus || "");
      window.Five9ChatSetVariable("Social.cohort", props.selected_cohort || props.cohort || "");
    }
  }, [
    props.universitySfId,
    props.selected_program,
    props.program,
    props.selected_campus,
    props.campus,
    props.selected_cohort,
    props.cohort,
  ]);

  useEffect(() => {
    if (typeof window !== "undefined" && window.Five9ChatSetContact) {
      window.Five9ChatSetContact("firstName", userInfo.userFirstName);
      window.Five9ChatSetContact("lastName", userInfo.userLastName);
      window.Five9ChatSetContact("email", userInfo.userEmail);
      window.Five9ChatSetContact("number1", userInfo.userPhoneNumber);
      window.Five9ChatSetVariable("Social.number", userInfo.userPhoneNumber);

      if (userInfo.userFirstName && userInfo.userLastName && userInfo.userEmail) {
        window.f9_postMessage({ type: "cancel-end" });
        window.Five9ChatAddAgentMessage(
          "We're here to help! Send us your message, and someone from our team will be with you shortly.",
          1,
        );
      }
    }
  }, [userInfo]);

  return null;
};

export default Five9Chat;
